import type { Stats } from '../../api'
import { CalibrationChart } from './CalibrationChart'
import { calibrationError } from './format'
import { Headline, PanelHeading } from './parts'

/**
 * How the published probabilities have held up against what happened.
 *
 * **This page leads with calibration, not accuracy.** A model can call 83% of matches correctly and
 * still be useless for betting if "70%" really means 55%, because the price you accept is set by the
 * number, not by whether the favourite won.
 *
 * It is one of three panels because it answers one of three questions. This one is "are the numbers
 * true"; *Versus the market* is "are they better than the bookmaker's", which is the question that
 * decides whether there is an edge at all; *Versions* is "whose numbers are these".
 */
export function RecordPanel({ stats }: { stats: Stats }) {
  const { overall } = stats

  if (!overall) {
    return (
      <p className="rounded border border-line bg-surface px-4 py-6 text-sm text-ink-soft">
        No scored predictions yet.
      </p>
    )
  }

  const error = calibrationError(stats.calibration)

  return (
    <div className="space-y-8">
      {/*
        THE HEADLINE IS IN THE READER'S LANGUAGE, and the scoring rules are one tap below it.

        The second thing on this page used to be `Log loss 0.635` and `Brier 0.222`, on a site whose
        readers are assumed to know betting and not machine learning — and with no glossary link
        anywhere on the page to get in. The baseline comparison did not rescue it: being told 0.635
        beats 0.693 tells a bettor the model is better than a coin, which they assumed, and nothing
        about whether to take a price.

        So the two figures a reader can act on lead, in sentences. Log loss and Brier are still here,
        exactly as before, behind a disclosure for whoever wants them.
      */}
      <section className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Headline
            figure={`${(error * 100).toFixed(1)} points`}
            // No verdict colour. This used to render red below 5 points, which reported a fault the
            // data does not show: 0.87 sigma across the whole table, Hosmer-Lemeshow p ~ 0.58, on an
            // estimator biased upward at this sample size.
            sentence={
              <>
                When we say a team wins, we are out by about{' '}
                <strong className="text-ink">{(error * 100).toFixed(0)} points</strong> on average —
                and no band is off by more than chance produces at this sample.
              </>
            }
            term="calibration"
            note={`over ${overall.count} predictions`}
          />
          <Headline
            figure={`${(overall.accuracy * 100).toFixed(0)}%`}
            sentence={
              <>
                We name the winner{' '}
                <strong className="text-ink">{(overall.accuracy * 100).toFixed(0)}%</strong> of the
                time, on {overall.count} maps. Useful, but not what decides whether a price is worth
                taking.
              </>
            }
            note={`${overall.correct} of ${overall.count} maps called`}
          />
        </div>

        <details className="rounded-lg border border-line bg-surface">
          <summary className="cursor-pointer px-4 py-2 text-xs text-ink-faint hover:text-ink-soft">
            The scoring rules, for the technically minded
          </summary>
          <div className="grid gap-3 border-t border-line p-4 sm:grid-cols-2">
            <Score
              label="Log loss"
              value={overall.logLoss.toFixed(3)}
              note={`vs ${overall.logLossBaseline.toFixed(3)} for a coin flip — punishes confident mistakes hardest`}
            />
            <Score
              label="Brier score"
              value={overall.brier.toFixed(3)}
              note={`vs ${overall.brierBaseline.toFixed(2)} for a coin flip — mean squared error on the probability`}
            />
          </div>
        </details>
      </section>

      <section>
        <PanelHeading
          lead="The question that matters more than accuracy: when the model says 70%, does it happen
            70% of the time? Each row is one band of predictions — where the two dots sit apart, the
            model claimed one thing and got another."
        >
          Reliability
        </PanelHeading>
        <div className="mt-3">
          <CalibrationChart bins={stats.calibration} />
        </div>
      </section>

      <section>
        <PanelHeading>When it commits</PanelHeading>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          {stats.byConfidence.map((band) => (
            <div key={band.band} className="rounded-lg border border-line bg-surface p-3">
              <div className="text-xs capitalize text-ink-faint">
                {band.band} · {(band.from * 100).toFixed(0)}–{(band.to * 100).toFixed(0)}%
              </div>
              {band.stats ? (
                <>
                  <div className="tabular mt-1 text-xl">
                    {(band.stats.accuracy * 100).toFixed(0)}%
                  </div>
                  <div className="mt-0.5 text-xs text-ink-faint">
                    {band.stats.correct}/{band.stats.count} · log loss{' '}
                    {band.stats.logLoss.toFixed(3)}
                  </div>
                </>
              ) : (
                <div className="mt-1 text-sm text-ink-faint">no predictions</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {stats.byPoint.length > 0 ? (
        <section>
          <PanelHeading>Before and after the draft</PanelHeading>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {stats.byPoint.map((point) => (
              <div key={point.point} className="rounded-lg border border-line bg-surface p-3">
                <div className="text-xs text-ink-faint">
                  {point.point === 'pre_draft'
                    ? 'Pre-draft — team strength only'
                    : 'Post-draft — heroes locked'}
                </div>
                <div className="tabular mt-1 text-xl">
                  {(point.stats.accuracy * 100).toFixed(0)}%
                </div>
                <div className="mt-0.5 text-xs text-ink-faint">
                  {point.stats.correct}/{point.stats.count} · log loss{' '}
                  {point.stats.logLoss.toFixed(3)}
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="space-y-3 text-sm text-ink-soft">
        <PanelHeading>What this model does not know</PanelHeading>
        <p>
          <strong className="text-ink">
            It knows less than the market, and that is the biggest caveat here.
          </strong>{' '}
          Our probabilities sit about half as far from evens as the sharp bookmaker&rsquo;s. That is
          the signature of a forecaster working from a smaller information set, and it has a
          consequence: a bet is picked as the widest gap between our number and the market&rsquo;s,
          so a compressed forecast lands on the underdog nearly every time and reports a double-digit
          edge while doing it. Simulated over 100,000 fixtures, a forecaster with{' '}
          <em>no miscalibration whatsoever</em> reproduces every one of those symptoms and still
          loses 7.2% in the long run. No amount of recalibration touches this, so treat the edges
          shown anywhere on this site as unproven.{' '}
          {/* A real link, because the panels are addressable — a bolded tab name that cannot be
              clicked is the shape of a cross-reference nobody follows. */}
          <a className="text-accent underline underline-offset-2" href="#/model/market">
            Versus the market
          </a>{' '}
          is where that claim stops being an argument and becomes a measurement.
        </p>
        <p>
          <strong className="text-ink">Two of its fifty features are not live.</strong> Both are a
          fitted regression over the hero picks and are fed a neutral value, so a post-draft number
          accounts for the ten heroes through every other feature but not through those two. A
          post-draft figure appears only where all ten heroes were actually seen before the horn;
          where they were not, there is no post-draft number rather than the pre-draft one wearing
          its label.
        </p>
        <p>
          <strong className="text-ink">Upcoming matches use an assumed roster.</strong> A schedule
          names teams; the model needs five account ids. We use the five who last played for that
          team, which is wrong precisely when a stand-in appears — and a stand-in is invisible to
          every source until the lobby opens. Each upcoming card states how strong that assumption is.
        </p>
      </section>
    </div>
  )
}

/**
 * A scoring rule beside the baseline it should beat.
 *
 * **No verdict colour.** These used to render green when they beat a coin flip and red otherwise,
 * which spends the outcome scale — the game's own green and red, reserved for a settled bet — on a
 * comparison that is not an outcome. A model losing to a coin flip is not a lost bet, and a reader
 * who has just seen red beside a lost stake should not meet the same red beside a log loss.
 */
function Score({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-3">
      <div className="text-xs text-ink-faint">{label}</div>
      <div className="tabular mt-1 text-xl text-ink">{value}</div>
      <div className="mt-0.5 text-xs text-ink-faint">{note}</div>
    </div>
  )
}
